import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@Reducers/index";
import { useNavigate, useSearchParams } from "react-router-dom";
import IconComponent from "../../cubeComponent/icons";
import {
  getProjectContractPageUrl,
  getProjectDashboardPageUrl,
  getProjectEstimatesPageUrl,
  getProjectFilesPageUrl,
  getProjectAssetsPageUrl,
  getProjectGeneralClashPageUrl,
  getProjectIssuesPageUrl,
  getProjectPhotosPageUrl,
  getProjectMailboxPageUrl,
  getProjectReviewsPageUrl,
  getProjectSchedulesPageUrl,
  getProjectTasksPageUrl,
  getProjectTransmittalsPageUrl,
  getProjectSettingsPageUrl,
  getProjectWorkspacesListPageUrl,
  getProjectFormsPageUrl,
} from "@Features/projects/utils/projectUrls";
import useStickySidebar from "./useStickySidebar";
import ProjectLayouts from "@Features/projects/components/ProjectLayouts";
import {
  PROJECT_DASHBOARD_SELECTED_LAYOUT_KEY,
  LAYOUTS_SIDEBAR_CELL_ID,
  SIDEBAR_LAYOUTS_TOGGLE_ATTR,
} from "@Helpers/constants";
import type { GetSelectedViewerLayoutResponse, LayoutData } from "@ServerTypes/layoutView/layoutView.schema";
import { getLayoutModuleIds } from "@Features/projects/utils/layoutModules";
import { getProjectObject } from "@Actions/projectSetting";
import { setSelectedProjectLayout } from "@Reducers/stickySidebar";
import axiosWrapper from "@Services/axiosWrapper";
import { useTranslation } from "@i18n";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_ERROR } from "@Services/constants";
import { getBackendUrl } from "@Utils";
import { Box, Chip, Divider, Drawer, List, ListItem, ListItemIcon } from "@mui/material";

interface SidebarProps {
  userData?: { company?: { name?: string } };
}

function Sidebar({ userData }: SidebarProps) {
  const {
    layout,
    currentSelectedSidebarCell: activeCell,
    selectedProjectLayout,
  } = useSelector((state: RootState) => state);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get("projectId");
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(true);
  const [showLayoutsModal, setShowLayoutsModal] = useState(false);
  const selectedLayoutId: string | undefined = selectedProjectLayout?._id;
  // Which projectId the applied selectedProjectLayout belongs to. selectedProjectLayout is global
  // (not keyed by project), so this lets us tell "layout for the current project is loaded" apart
  // from "stale / still loading" — without flashing a fallback list in between.
  const [layoutLoadedForProjectId, setLayoutLoadedForProjectId] = useState<string | null>(null);
  // Module ids come straight from the stored layout doc; custom module names are
  // embedded server-side at write time.
  const selectedLayoutModules = useMemo(() => getLayoutModuleIds(selectedProjectLayout), [selectedProjectLayout]);
  const [showScrollUp, setShowScrollUp] = useState(false);
  const [showScrollDown, setShowScrollDown] = useState(true);

  const midIconsDivRef: any = useRef<any>(null);

  const { sideBarMidIcons, sideBarUpperFixedIcons, sideBarLowerFixedIcons }: any = useStickySidebar({
    layout,
    projectId,
    path: {
      Contract: (projectId: string) => getProjectContractPageUrl(projectId),
      Dashboard: (projectId: string) => getProjectDashboardPageUrl(projectId),
      Estimates: (projectId: string) => getProjectEstimatesPageUrl(projectId),
      Files: (projectId: string) => getProjectFilesPageUrl(projectId),
      Assets: (projectId: string) => getProjectAssetsPageUrl(projectId),
      GeneralClash: (projectId: string) => getProjectGeneralClashPageUrl(projectId),
      Issues: (projectId: string) => getProjectIssuesPageUrl(projectId),
      Photos: (projectId: string) => getProjectPhotosPageUrl(projectId),
      Mailbox: (projectId: string) => getProjectMailboxPageUrl(projectId),
      Reviews: (projectId: string) => getProjectReviewsPageUrl(projectId),
      Schedules: (projectId: string) => getProjectSchedulesPageUrl(projectId),
      Tasks: (projectId: string) => getProjectTasksPageUrl(projectId),
      Transmittals: (projectId: string) => getProjectTransmittalsPageUrl(projectId),
      Settings: (projectId: string) => getProjectSettingsPageUrl({ projectId, tab: "general_information" }),
      WorkspacesList: (projectId: string) => getProjectWorkspacesListPageUrl(projectId),
      Forms: (projectId: string, moduleName: string) => getProjectFormsPageUrl(projectId, moduleName),
    },
  });

  const visibleMidIcons = useMemo(() => {
    // Until the selected layout for THIS project has resolved, render nothing — NOT the "All Modules"
    // fallback. Showing every module during the fetch window and then narrowing to the saved subset
    // is the visible flicker (blank → all modules → subset); painting once, when the layout is ready,
    // removes it. Staleness is tracked by projectId, so a project switch (or a re-run of the fetch
    // effect) never briefly shows the previous project's list or the full module set.
    if (layoutLoadedForProjectId !== projectId) return [];
    // Resolved, but no usable selection (deleted selection or a transient fetch failure) → fall back
    // to every enabled module so the sidebar is never permanently empty.
    if (!selectedProjectLayout) return sideBarMidIcons;
    return Array.isArray(selectedLayoutModules)
      ? selectedLayoutModules
          .map((moduleId: string) => sideBarMidIcons.find((icon: { id: string }) => icon.id === moduleId))
          .filter(Boolean)
      : [];
  }, [layoutLoadedForProjectId, projectId, selectedProjectLayout, selectedLayoutModules, sideBarMidIcons]);

  useEffect(() => {
    const sideBarState: any = localStorage.getItem("isSideBarExpanded");
    setIsExpanded(!sideBarState);
  }, [sideBarMidIcons]);

  useEffect(() => {
    const el = midIconsDivRef.current;
    if (!el) return;

    const update = () => {
      const canScrollUp = el.scrollTop > 0;
      const canScrollDown = el.scrollHeight - el.clientHeight > el.scrollTop;

      setShowScrollUp(canScrollUp);
      setShowScrollDown(canScrollDown);
    };

    // 1. Observe layout / size changes
    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(el);

    // 2. Observe scroll changes
    el.addEventListener("scroll", update, { passive: true });

    // 3. Initial check AFTER mount
    requestAnimationFrame(update);

    return () => {
      el.removeEventListener("scroll", update);
      resizeObserver.disconnect();
    };
  }, [midIconsDivRef, isExpanded, visibleMidIcons.length]);

  const handleUpArrowClick = () => {
    const scrollableContent = midIconsDivRef.current;
    if (scrollableContent) {
      scrollableContent.scrollBy({ top: -100, behavior: "smooth" });
    }
  };

  const handleDownArrowClick = (customHeight = 100) => {
    const scrollableContent = midIconsDivRef.current;
    if (scrollableContent) {
      scrollableContent.scrollBy({ top: customHeight, behavior: "smooth" });
    }
  };

  const applySelectedLayout = useCallback(
    (layoutDoc: LayoutData | null) => {
      dispatch(setSelectedProjectLayout(layoutDoc ?? null));
    },
    [dispatch],
  );

  // Stable identity so ProjectLayouts' outside-click listener attaches once per open/close of the
  // flyout rather than re-subscribing on every Sidebar render.
  const closeLayoutsModal = useCallback(() => setShowLayoutsModal(false), []);

  useEffect(() => {
    if (!projectId) return;
    dispatch(getProjectObject({ id: projectId }));
    let active = true;
    (async () => {
      try {
        const response = await axiosWrapper.post<GetSelectedViewerLayoutResponse>(
          `${getBackendUrl()}/layoutView/getSelectedViewerLayout`,
          { key: PROJECT_DASHBOARD_SELECTED_LAYOUT_KEY, projectId },
        );
        if (!active) return;
        applySelectedLayout(response.data?.data ?? null);
      } catch {
        if (!active) return;
        // On a transient failure fall back to the "All Modules" default (visibleMidIcons treats a null
        // selection as "show everything"), and let the user know it couldn't load.
        applySelectedLayout(null);
        dispatch(showToastNotification({ content: t("Something went wrong"), type: TOAST_TYPE_ERROR }));
      } finally {
        // Mark the selection resolved for THIS project (success or handled failure) — visibleMidIcons
        // stays blank until this matches the current projectId, so no fallback flashes mid-fetch. We
        // intentionally do NOT reset the layout to null up-front: keeping the last one applied means a
        // re-run of this effect (or an unrelated re-render) never re-flashes the sidebar.
        if (active) setLayoutLoadedForProjectId(projectId);
      }
    })();
    return () => {
      active = false;
    };
  }, [projectId, applySelectedLayout, dispatch, t]);

  const [showSettings, setShowSettings] = useState(true);

  useEffect(() => {
    if (layout.PROJECT_SETTINGS?.ENABLE === false) {
      setShowSettings(false);
    } else if (
      typeof layout.PROJECT_SETTINGS?.ENABLE === "object" &&
      layout.PROJECT_SETTINGS?.ENABLE?.CONDITION?.COMPANY_NAME
    ) {
      setShowSettings(userData?.company?.name == layout.PROJECT_SETTINGS?.ENABLE?.CONDITION?.COMPANY_NAME);
    }
  }, [layout, userData]);

  const handleSideBarExpandClick = () => {
    if (isExpanded) {
      localStorage.setItem("isSideBarExpanded", "no");
      setIsExpanded(false);
    } else {
      localStorage.removeItem("isSideBarExpanded");
      setIsExpanded(true);
    }
  };

  const handleRowClick = (id: string, path: any) => {
    if (id === LAYOUTS_SIDEBAR_CELL_ID) {
      setShowLayoutsModal((prev) => !prev);
      return;
    }
    navigate(path);
  };

  const getBetaTag = () => (
    <Chip
      size="small"
      sx={{
        fontWeight: 400,
        background: "#666a84",
        color: "white",
      }}
      label="BETA"
    />
  );
  const getIconsList = ({ iconsList, type, ref = undefined, css }: any) => {
    return (
      <List
        ref={ref}
        sx={{
          paddingBottom: "0.625rem",
          border: "none",
          display: "flex",
          paddingTop: 0,
          flexDirection: "column",
          gap: "0",
          ...css,
        }}
      >
        {iconsList.map(
          ({ allow, toolTip, path, name, svg, id, showBetaTag }: any) =>
            allow && (
              <ListItem
                key={id}
                disablePadding
                data-testid={name + "_SidebarIcons"}
                // Gate only the Settings icon when Project Settings is disabled — never the whole
                // bottom group, so sibling icons like Layouts stay clickable.
                sx={{
                  opacity: type == "settings" && id === "Settings" && !showSettings ? 0.5 : 1,
                  pointerEvents: type == "settings" && id === "Settings" && !showSettings ? "none" : undefined,
                }}
                {...(id === LAYOUTS_SIDEBAR_CELL_ID ? { [SIDEBAR_LAYOUTS_TOGGLE_ATTR]: "true" } : {})}
              >
                <div className="flex justify-center py-4 items-center ps-2">
                  {activeCell === id ? (
                    <div
                      style={{ borderStartEndRadius: "0.375rem", borderEndEndRadius: "0.375rem" }}
                      className={`w-2 h-3 bg-[linear-gradient(#6bbbe3,_#3e8ab0)] absolute start-0`}
                    ></div>
                  ) : null}
                  <ListItemIcon sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <IconComponent
                      position="right"
                      onClick={() => handleRowClick(id, path)}
                      toolTipContent={toolTip}
                      showGradient={activeCell === id}
                      name={svg}
                      menuShow={isExpanded}
                      parentContainerClass={`flex gap-5 ${isExpanded ? "ps-4" : ""}`}
                      width="1.5rem"
                      height="1.5rem"
                    >
                      <div className="flex gap-3 items-center">
                        <span
                          className={`text-sm text-[var(--primary-text)] ${activeCell === id ? "text-primary_gradient_color1" : ""}`}
                        >
                          {name}
                        </span>
                        {showBetaTag && getBetaTag()}
                      </div>
                    </IconComponent>
                  </ListItemIcon>
                </div>
              </ListItem>
            ),
        )}
      </List>
    );
  };

  function getDrawer() {
    return (
      <Drawer
        sx={{
          width: isExpanded ? "15.625rem" : "4.5rem",
          "& .MuiDrawer-paper": {
            width: isExpanded ? "15.625rem" : "4.5rem",
            zIndex: 9,
            position: "fixed",
            paddingTop: "6.25rem",
            height: "100%",
            border: "none",
            display: "flex",
            overflow: "auto",
            backgroundColor: "#f0f5f9",
          },
        }}
        variant="permanent"
        open={isExpanded}
        anchor={"left"}
      >
        <div>
          {getIconsList({ iconsList: sideBarUpperFixedIcons, height: "28.125rem", css: {} })}
          <Divider />
        </div>
        <div className="flex overflow-auto flex-col h-full">
          {showScrollUp && (
            <Box
              onClick={handleUpArrowClick}
              id="upArrow"
              className={`flex cursor-pointer bg-surface_neutral p-3 justify-center items-center`}
            >
              <IconComponent name="BoldUpArrow" width="1rem" height="1rem" />
            </Box>
          )}
          {getIconsList({
            iconsList: visibleMidIcons,
            type: "midIcons",
            ref: midIconsDivRef,
            css: { overflow: "hidden", flex: 1, minHeight: 0 },
          })}
          {showScrollDown && (
            <Box
              onClick={() => handleDownArrowClick()}
              className={`cursor-pointer flex bg-surface_neutral justify-center items-center p-3`}
            >
              <IconComponent name="BoldDownArrow" width="1rem" height="1rem" />
            </Box>
          )}
        </div>
        <div className="relative">
          <Divider />
          {getIconsList({ iconsList: sideBarLowerFixedIcons, type: "settings", css: { paddingTop: "0.875rem" } })}
          {showLayoutsModal && (
            <ProjectLayouts
              onClose={closeLayoutsModal}
              midIcons={sideBarMidIcons}
              projectId={projectId}
              isExpanded={isExpanded}
              selectedLayoutId={selectedLayoutId}
              onLayoutSelected={applySelectedLayout}
            />
          )}
        </div>
      </Drawer>
    );
  }

  return (
    <div
      className={`relative flex h-full bg-[#eff5f9] transition-[width] duration-300 ${
        localStorage.getItem("isSideBarExpanded") ? "w-20" : "w-[15.625rem]"
      }`}
    >
      {getDrawer()}
      <Box
        onClick={handleSideBarExpandClick}
        className={
          isExpanded
            ? "h-[var(--size-35)] cursor-pointer border-e-[length:var(--size-2)] border-e-[var(--text-light)] border-solid me-[0.8125rem] opacity-30 absolute top-1/2 bottom-1/2 ps-[var(--size-8)] z-10 end-[var(--size-2)] hover:cursor-pointer hover:border-none hover:bg-no-repeat hover:opacity-100 hover:bg-[url('@Assets/icons/svg/BigLeftArrow.svg')]"
            : "pe-[var(--size-8)] h-[var(--size-35)] cursor-pointer border-s-[length:var(--size-2)] border-s-[var(--text-light)] border-solid opacity-30 z-[11] top-1/2 absolute bottom-1/2 start-[var(--size-65)] hover:cursor-pointer hover:border-none hover:opacity-100 hover:bg-no-repeat hover:bg-[url('@Assets/icons/svg/BigRightArrow.svg')]"
        }
      />
    </div>
  );
}

export default Sidebar;
